import React, { useState, useEffect, useCallback, useMemo } from 'react';
import secureStorage from '../../lib/secureStorage';
import authedFetch from '../../lib/authedFetch';
import { AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Brain, Loader2, TrendingUp, TrendingDown, Target, CheckCircle2, AlertTriangle, Sliders, Sparkles, RefreshCw, FileText, ArrowRight, Pin } from 'lucide-react';
import { toast } from 'sonner';
import { takeAction } from '../health/HealthActionSpine';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function money(n) {
  if (n == null) return '—';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n)}`;
}

const clamp = (v) => Math.round(Math.max(0, Math.min(100, v)) * 10) / 10;

// Linear regression + residual-std over a [{month, lei}] series.
function regress(series) {
  const n = series.length;
  const xs = series.map((_, i) => i);
  const ys = series.map((p) => p.lei);
  const sx = xs.reduce((a, b) => a + b, 0), sy = ys.reduce((a, b) => a + b, 0);
  const sxy = xs.reduce((a, x, i) => a + x * ys[i], 0), sxx = xs.reduce((a, x) => a + x * x, 0);
  const slope = (n * sxy - sx * sy) / (n * sxx - sx * sx || 1);
  const intercept = (sy - slope * sx) / n;
  const resid = ys.map((y, i) => y - (intercept + slope * i));
  const std = Math.sqrt(resid.reduce((a, r) => a + r * r, 0) / Math.max(1, n - 1)) || 2;
  return { n, ys, slope, intercept, std, project: (i) => clamp(intercept + slope * i) };
}

export default function ForecastView({ organizationId, isDark, navigate }) {
  const [loading, setLoading] = useState(true);
  const [entities, setEntities] = useState([]);
  const [selectedId, setSelectedId] = useState('portfolio');
  const [showBand, setShowBand] = useState(true);
  const [horizon, setHorizon] = useState(3);
  const [scenario, setScenario] = useState('all'); // all | expected | best | worst
  const [goal, setGoal] = useState('');
  const [whatIf, setWhatIf] = useState(0);
  const [interventions, setInterventions] = useState([]);
  const [newName, setNewName] = useState('');
  const [newLift, setNewLift] = useState('');
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightCached, setInsightCached] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const nudgedRef = React.useRef(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const [invRes, progRes, cohRes] = await Promise.all([
        authedFetch(`${API_URL}/api/lei-health/investment?${params}`, h),
        authedFetch(`${API_URL}/api/lei-health/program-pillars?${params}`, h),
        authedFetch(`${API_URL}/api/lei-health/cohort-pillars?${params}`, h),
      ]);
      const inv = invRes.ok ? await invRes.json() : null;
      const prog = progRes.ok ? await progRes.json() : null;
      const coh = cohRes.ok ? await cohRes.json() : null;

      const list = [];
      const portSeries = (inv?.value_trend || []).filter((p) => p.lei != null).map((p) => ({ month: p.month, lei: p.lei }));
      if (portSeries.length >= 2) {
        list.push({ id: 'portfolio', label: 'Whole portfolio', kind: 'portfolio', series: portSeries, cost: inv?.totals?.avg_cost_per_point || null });
      }
      const toSeries = (t) => (t || []).filter((p) => p.score != null).map((p) => ({ month: p.month, lei: p.score }));
      (prog?.cohorts || []).forEach((p) => {
        const s = toSeries(p.trend);
        if (s.length >= 2) list.push({ id: `prog-${p.cohort_id}`, label: p.cohort_name, kind: 'program', series: s, cost: p.cost_per_lei_point || null, investment: p.investment || null });
      });
      (coh?.cohorts || []).forEach((c) => {
        const s = toSeries(c.trend);
        if (s.length >= 2) list.push({ id: `cohort-${c.cohort_id}`, label: c.cohort_name, kind: 'cohort', series: s, cost: c.cost_per_lei_point || null, investment: c.investment || null });
      });
      setEntities(list);
      setSelectedId((prev) => (list.some((e) => e.id === prev) ? prev : (list[0]?.id || 'portfolio')));
    } catch (e) { setEntities([]); } finally { setLoading(false); }
  }, [organizationId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const selected = useMemo(() => entities.find((e) => e.id === selectedId) || null, [entities, selectedId]);

  // Load the persisted goal + reset what-if whenever the selected entity changes.
  useEffect(() => {
    if (!selected) return;
    const saved = localStorage.getItem(`lei_forecast_goal_${selected.id}`);
    setGoal(saved != null ? saved : '');
    setWhatIf(0);
    const plan = localStorage.getItem(`lei_planned_intervention_${selected.id}`);
    let list = [];
    try {
      const saved = localStorage.getItem(`lei_planned_interventions_${selected.id}`);
      if (saved) list = JSON.parse(saved) || [];
    } catch (e) { list = []; }
    if (!list.length && plan != null && !Number.isNaN(Number(plan)) && Number(plan) !== 0) {
      list = [{ name: 'Modeled intervention', lift: Number(plan) }];
    }
    setInterventions(list);
    setNewName(''); setNewLift('');
  }, [selected]);

  const onGoalChange = (v) => {
    setGoal(v);
    if (selected) {
      if (v === '') localStorage.removeItem(`lei_forecast_goal_${selected.id}`);
      else localStorage.setItem(`lei_forecast_goal_${selected.id}`, v);
    }
  };

  const runInsight = async ({ force = false, cachedOnly = false } = {}) => {
    if (!selected) return;
    if (!cachedOnly) setInsightLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/lei-health/forecast-insight`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: organizationId, entity_id: selected.id, horizon, force, cached_only: cachedOnly }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d && d.available !== false && d.insight) { setInsight(d.insight); setInsightCached(!!d.cached); }
        else if (!cachedOnly) toast.error('Could not generate the insight. Please retry.');
      } else if (!cachedOnly) toast.error('Could not generate the insight. Please retry.');
    } catch (e) { if (!cachedOnly) toast.error('Could not generate the insight. Please retry.'); }
    finally { if (!cachedOnly) setInsightLoading(false); }
  };

  const loadHistory = async () => {
    if (!selected) return;
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams({ entity_id: selected.id });
      if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
      const res = await authedFetch(`${API_URL}/api/lei-health/forecast-insight/history?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setHistory((await res.json()).history || []);
    } catch (e) { /* best-effort */ }
  };
  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) loadHistory();
  };

  // Auto-load a cached insight (no LLM spend) whenever the entity / horizon changes.
  useEffect(() => {
    setInsight(null); setInsightCached(false); setShowHistory(false); setHistory([]);
    if (selected) runInsight({ cachedOnly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, horizon, entities.length]);

  // Eval-on-load goal nudge: if the selected entity's goal is off track, ping
  // the backend to create a deduped in-app notification (fires once per session key).
  useEffect(() => {
    if (!selected) return;
    const g = goal !== '' && !Number.isNaN(Number(goal)) ? Number(goal) : null;
    if (g == null) return;
    const R2 = regress(selected.series);
    const cur = R2.ys[R2.n - 1];
    if (cur >= g) return;
    const months = R2.slope > 0.01 ? Math.ceil((g - cur) / R2.slope) : null;
    const offTrack = !(months != null && months <= horizon);
    if (!offTrack) return;
    const key = `${selected.id}:${g}:${horizon}`;
    if (nudgedRef.current.has(key)) return;
    nudgedRef.current.add(key);
    (async () => {
      try {
        const token = secureStorage.getItem('session_token');
        await authedFetch(`${API_URL}/api/lei-health/goal-nudge`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ organization_id: organizationId, entity_id: selected.id, entity_label: selected.label, goal: g, current: Math.round(cur), projected: R2.project(R2.n - 1 + horizon), months_to_goal: months }),
        });
      } catch (e) { /* nudge is best-effort */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, goal, horizon]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
      if (selected) params.append('entity_id', selected.id);
      params.append('horizon', String(horizon));
      const res = await authedFetch(`${API_URL}/api/lei-health/forecast/pdf?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = 'lei-forecast-scenarios.pdf'; a.click(); URL.revokeObjectURL(url);
      toast.success('Forecast PDF exported');
    } catch (e) { toast.error('Could not generate the PDF'); } finally { setExporting(false); }
  };

  const modelGoal = (goalNum, current) => {
    if (!navigate || !selected) return;
    const gap = Math.max(1, Math.round((goalNum - current) * 10) / 10);
    takeAction({
      engine: 'lei', entityId: 'lei', entityLabel: `${selected.label} → LEI ${goalNum}`,
      pillarId: null, score: Math.round(current), target: goalNum, gapPct: gap, lift: gap, granularity: 'entity',
    }, 'model', navigate);
  };

  if (loading) return <div className="flex justify-center py-20" data-testid="forecast-loading"><Loader2 className={cn('h-6 w-6 animate-spin', isDark ? 'text-slate-400' : 'text-slate-500')} /></div>;
  if (!selected) {
    return <div className={cn('text-center py-16 rounded-2xl border', isDark ? 'bg-[#0d1526] border-[#22304d] text-slate-400' : 'bg-white border-slate-200 text-slate-500')} data-testid="forecast-empty"><Brain className="h-8 w-8 mx-auto mb-3 opacity-50" /><p className="text-sm">Not enough history to forecast yet.</p></div>;
  }

  const series = selected.series;
  const planLift = interventions.reduce((a, b) => a + (Number(b.lift) || 0), 0);
  const persistInterventions = (list) => {
    setInterventions(list);
    try {
      if (list.length) localStorage.setItem(`lei_planned_interventions_${selected.id}`, JSON.stringify(list));
      else localStorage.removeItem(`lei_planned_interventions_${selected.id}`);
    } catch (e) { /* ignore */ }
  };
  const addIntervention = () => {
    let lift = Number(newLift);
    if (!newName.trim() || Number.isNaN(lift) || lift === 0) return;
    lift = Math.max(0, Math.min(40, lift));
    persistInterventions([...interventions, { name: newName.trim().slice(0, 40), lift }]);
    setNewName(''); setNewLift('');
  };
  const removeIntervention = (i) => persistInterventions(interventions.filter((_, idx) => idx !== i));
  const R = regress(series);
  const n = R.n;
  const NEXT = Array.from({ length: horizon }, (_, i) => `+${i + 1}mo`);
  const data = [
    ...series.map((p, i) => ({ month: p.month, actual: p.lei, x: i })),
    ...NEXT.map((lbl, j) => {
      const f = R.project(n - 1 + (j + 1));
      const w = Math.round(R.std * 1.5 * Math.sqrt(j + 1) * 10) / 10;
      const lo = clamp(f - w);
      const hi = clamp(f + w);
      const ramp = Math.min(1, (j + 1) / 2);
      return {
        month: lbl,
        expected: f,
        best: hi,
        worst: lo,
        range: [lo, hi],
        planned: planLift ? clamp(f + planLift * ramp) : undefined,
      };
    }),
  ];
  // Connect the actual line to the forecast lines at the "now" point.
  data[n - 1].expected = series[n - 1].lei;
  data[n - 1].best = series[n - 1].lei;
  data[n - 1].worst = series[n - 1].lei;
  data[n - 1].range = [series[n - 1].lei, series[n - 1].lei];
  if (planLift) data[n - 1].planned = series[n - 1].lei;

  const current = R.ys[n - 1];
  const projected = R.project(n - 1 + horizon);
  const delta = Math.round((projected - current) * 10) / 10;
  const up = delta >= 0;
  const bandW = Math.round(R.std * 1.5 * Math.sqrt(horizon) * 10) / 10;
  const bestProjected = clamp(projected + bandW);
  const worstProjected = clamp(projected - bandW);

  // Goal-line + on-track evaluation.
  const goalNum = goal !== '' && !Number.isNaN(Number(goal)) ? Number(goal) : null;
  let goalStatus = null;
  if (goalNum != null) {
    if (current >= goalNum) {
      goalStatus = { ok: true, text: `Already at or above your ${goalNum} target (current ${Math.round(current)}).` };
    } else if (R.slope > 0.01) {
      const monthsToGoal = Math.ceil((goalNum - current) / R.slope);
      if (monthsToGoal <= horizon) {
        goalStatus = { ok: true, text: `On track — projected to reach ${goalNum} in ~${monthsToGoal} month${monthsToGoal === 1 ? '' : 's'}.` };
      } else {
        goalStatus = { ok: false, text: `Off track — reaching ${goalNum} would take ~${monthsToGoal} months at the current pace (${projected} projected in ${horizon}mo).` };
      }
    } else {
      goalStatus = { ok: false, text: `Off track — trend is flat or declining, so ${goalNum} is not reachable without intervention.` };
    }
  }

  // What-if investment simulator with diminishing returns: extra spend buys
  // LEI points asymptotically approaching the remaining headroom (100 - projected),
  // so you can never overshoot 100 and each extra dollar buys progressively less.
  const cost = selected.cost;
  const headroom = Math.max(0, 100 - projected);
  const rawLift = cost && whatIf > 0 ? whatIf / cost : 0;
  const leiLift = headroom > 0 ? headroom * (1 - Math.exp(-rawLift / Math.max(1, headroom))) : 0;
  const adjustedProjected = clamp(projected + leiLift);
  const diminishing = rawLift - leiLift > 0.5; // linear estimate outran the curve
  const whatIfMax = 1000000;

  const showBest = scenario === 'all' || scenario === 'best';
  const showWorst = scenario === 'all' || scenario === 'worst';

  const yMin = Math.max(0, Math.floor(Math.min(...R.ys, worstProjected, goalNum ?? 100) - 8));
  const yMax = Math.min(100, Math.ceil(Math.max(...R.ys, bestProjected, adjustedProjected, clamp(projected + planLift), goalNum ?? 0) + 8));

  const card = cn('rounded-2xl border p-5', isDark ? 'bg-[#0d1526] border-[#22304d]' : 'bg-white border-slate-200');
  const chipBase = 'text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors';
  const SCEN = [
    { id: 'all', label: 'All scenarios' },
    { id: 'expected', label: 'Expected' },
    { id: 'best', label: 'Best case' },
    { id: 'worst', label: 'Worst case' },
  ];

  return (
    <div className="space-y-4" data-testid="forecast-view">
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={async () => {
            try {
              const token = secureStorage.getItem('session_token');
              await authedFetch(`${API_URL}/api/quarterly-deck/pins`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  path: '/lei-health/forecast',
                  title: `LEI forecast: ${selected.label}`,
                  description: `${horizon}-month LEI projection — expected ${projected} (range ${worstProjected}–${bestProjected}).`,
                  theme: 'lei-forecast', origin_engine: 'lei', origin_label: `${selected.kind} forecast`,
                  stats: [
                    { label: 'Current', value: String(current) },
                    { label: 'Expected', value: String(projected) },
                    { label: 'Best', value: String(bestProjected) },
                    { label: 'Worst', value: String(worstProjected) },
                  ],
                }),
              });
              toast.success('Forecast added to Quarterly Deck');
            } catch (e) { toast.error('Could not add to the deck'); }
          }}
          className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border', isDark ? 'border-[#22304d] text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}
          data-testid="forecast-pin-deck"
        >
          <Pin className="h-3.5 w-3.5" /> Add to Quarterly Deck
        </button>
        <button onClick={exportPdf} disabled={exporting} className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border', isDark ? 'border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10' : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50')} data-testid="forecast-export-pdf">
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Export scenario PDF
        </button>
      </div>
      {/* Controls */}
      <div className={card}>
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-purple-500" />
            <h3 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>Predictive LEI forecast</h3>
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-bold tabular-nums" style={{ color: up ? '#10b981' : '#f43f5e' }} data-testid="forecast-delta">
            {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}{delta > 0 ? '+' : ''}{delta} pts in {horizon}mo
          </span>
        </div>

        {/* Entity picker + horizon + band */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className={cn('text-[11px] font-semibold uppercase tracking-wide', isDark ? 'text-slate-500' : 'text-slate-400')}>Forecast for</span>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className={cn('text-xs font-semibold px-2.5 py-1.5 rounded-lg border outline-none', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700')}
              data-testid="forecast-entity-select"
            >
              {entities.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.kind === 'portfolio' ? e.label : `${e.kind === 'program' ? 'Program' : 'Cohort'}: ${e.label}`}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1" />
          <button onClick={() => setShowBand((v) => !v)} className={cn(chipBase, showBand ? (isDark ? 'bg-purple-500/15 border-purple-500/40 text-purple-200' : 'bg-purple-50 border-purple-300 text-purple-700') : (isDark ? 'border-[#22304d] text-slate-400' : 'border-slate-200 text-slate-500'))} data-testid="forecast-band-toggle">Confidence band: {showBand ? 'on' : 'off'}</button>
          <div className={cn('inline-flex items-center rounded-lg border p-0.5 text-[11px] font-semibold', isDark ? 'border-[#22304d] bg-[#0b1220]' : 'border-slate-200 bg-slate-50')} data-testid="forecast-horizon-toggle">
            {[3, 6].map((m) => (
              <button key={m} onClick={() => setHorizon(m)} className={cn('px-2.5 py-1 rounded-md', horizon === m ? (isDark ? 'bg-cyan-500/20 text-cyan-200' : 'bg-white text-cyan-700 shadow-sm') : (isDark ? 'text-slate-400' : 'text-slate-500'))} data-testid={`forecast-horizon-${m}`}>{m}mo</button>
            ))}
          </div>
        </div>

        {/* Scenario toggle */}
        <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="forecast-scenario-toggle">
          <span className={cn('text-[11px] font-semibold uppercase tracking-wide mr-1', isDark ? 'text-slate-500' : 'text-slate-400')}>Scenario</span>
          {SCEN.map((s) => (
            <button
              key={s.id}
              onClick={() => setScenario(s.id)}
              className={cn(chipBase, scenario === s.id ? (isDark ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-200' : 'bg-cyan-50 border-cyan-300 text-cyan-700') : (isDark ? 'border-[#22304d] text-slate-400 hover:text-slate-200' : 'border-slate-200 text-slate-500 hover:text-slate-700'))}
              data-testid={`forecast-scenario-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <p className={cn('text-xs mb-3', isDark ? 'text-slate-500' : 'text-slate-400')}>Projection is a linear trend over real LEI history. Best / worst cases are ±1.5σ of residuals, widening with the horizon.</p>

        <div className="mb-3" data-testid="forecast-planned-intervention">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={cn('text-[11px] font-semibold uppercase tracking-wide', isDark ? 'text-slate-500' : 'text-slate-400')}>Planned interventions</span>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Q3 Leadership Sprint"
              className={cn('flex-1 min-w-[140px] text-xs px-2 py-1 rounded-lg border outline-none', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700')}
              data-testid="forecast-plan-name-input" />
            <input type="number" min="0" max="40" value={newLift} onChange={(e) => setNewLift(e.target.value)} placeholder="+LEI"
              className={cn('w-20 text-xs px-2 py-1 rounded-lg border outline-none', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700')}
              data-testid="forecast-plan-lift-input" />
            <button onClick={addIntervention} className={cn('text-xs font-semibold px-2.5 py-1 rounded-lg', isDark ? 'bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30' : 'bg-emerald-600 text-white hover:bg-emerald-700')} data-testid="forecast-plan-add">Add</button>
          </div>
          {interventions.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="forecast-plan-list">
              {interventions.map((iv, i) => (
                <span key={i} className={cn('inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border', isDark ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-emerald-50 border-emerald-200 text-emerald-700')} data-testid={`forecast-plan-chip-${i}`}>
                  {iv.name} <b>+{iv.lift}</b>
                  <button onClick={() => removeIntervention(i)} className="ml-0.5 opacity-60 hover:opacity-100" data-testid={`forecast-plan-remove-${i}`}>×</button>
                </span>
              ))}
              <span className="text-xs font-semibold text-emerald-600">Combined +{planLift} → {clamp(projected + planLift)} by +{horizon}mo</span>
            </div>
          )}
        </div>

        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id="fA" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.4} /><stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.05} /></linearGradient>
                <linearGradient id="fF" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#a855f7" stopOpacity={0.35} /><stop offset="95%" stopColor="#a855f7" stopOpacity={0.03} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1f2a44' : '#eef2f7'} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
              <YAxis domain={[yMin, yMax]} tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div className={cn('rounded-lg border p-2 text-xs', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200' : 'bg-white border-slate-200 text-slate-700')}>
                    <b>{p.month}</b>
                    {p.actual != null ? <div>{p.actual} LEI</div> : (
                      <>
                        <div>Expected: {p.expected}</div>
                        {p.best != null && <div className="text-emerald-500">Best: {p.best}</div>}
                        {p.worst != null && <div className="text-rose-500">Worst: {p.worst}</div>}
                      </>
                    )}
                  </div>
                );
              }} />
              <ReferenceLine x={series[n - 1].month} stroke={isDark ? '#475569' : '#cbd5e1'} strokeDasharray="4 4" label={{ value: 'now', position: 'top', fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
              {goalNum != null && <ReferenceLine y={goalNum} stroke="#f59e0b" strokeDasharray="5 3" label={{ value: `Goal ${goalNum}`, position: 'right', fontSize: 9, fill: '#f59e0b' }} data-testid="forecast-goal-line" />}
              {whatIf > 0 && leiLift > 0 && <ReferenceLine y={adjustedProjected} stroke="#10b981" strokeDasharray="2 2" label={{ value: `+$ → ${adjustedProjected}`, position: 'insideRight', fontSize: 9, fill: '#10b981' }} />}
              {showBand && <Area type="monotone" dataKey="range" stroke="none" fill={isDark ? '#a855f7' : '#c4b5fd'} fillOpacity={0.18} isAnimationActive={false} connectNulls name="Confidence range" />}
              <Area type="monotone" dataKey="actual" stroke="#0ea5e9" strokeWidth={2} fill="url(#fA)" connectNulls name="Actual LEI" />
              <Area type="monotone" dataKey="expected" stroke="#a855f7" strokeWidth={2} strokeDasharray="6 4" fill="url(#fF)" connectNulls name="Expected" />
              {showBest && <Line type="monotone" dataKey="best" stroke="#10b981" strokeWidth={1.75} strokeDasharray="4 3" dot={false} connectNulls name="Best case" isAnimationActive={false} />}
              {showWorst && <Line type="monotone" dataKey="worst" stroke="#f43f5e" strokeWidth={1.75} strokeDasharray="4 3" dot={false} connectNulls name="Worst case" isAnimationActive={false} />}
              {planLift > 0 && <Line type="monotone" dataKey="planned" stroke="#10b981" strokeWidth={2.25} dot={false} connectNulls name="With planned intervention" isAnimationActive={false} />}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Scenario projected values */}
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div className={cn('rounded-xl border p-2.5 text-center', isDark ? 'bg-rose-500/10 border-rose-500/25' : 'bg-rose-50 border-rose-200')} data-testid="forecast-worst-value">
            <p className={cn('text-[10px] font-bold uppercase tracking-wide', isDark ? 'text-rose-300/80' : 'text-rose-600')}>Worst case</p>
            <p className={cn('text-lg font-extrabold tabular-nums', isDark ? 'text-rose-200' : 'text-rose-700')}>{worstProjected}</p>
          </div>
          <div className={cn('rounded-xl border p-2.5 text-center', isDark ? 'bg-purple-500/10 border-purple-500/25' : 'bg-purple-50 border-purple-200')} data-testid="forecast-expected-value">
            <p className={cn('text-[10px] font-bold uppercase tracking-wide', isDark ? 'text-purple-300/80' : 'text-purple-600')}>Expected</p>
            <p className={cn('text-lg font-extrabold tabular-nums', isDark ? 'text-purple-200' : 'text-purple-700')}>{projected}</p>
          </div>
          <div className={cn('rounded-xl border p-2.5 text-center', isDark ? 'bg-emerald-500/10 border-emerald-500/25' : 'bg-emerald-50 border-emerald-200')} data-testid="forecast-best-value">
            <p className={cn('text-[10px] font-bold uppercase tracking-wide', isDark ? 'text-emerald-300/80' : 'text-emerald-600')}>Best case</p>
            <p className={cn('text-lg font-extrabold tabular-nums', isDark ? 'text-emerald-200' : 'text-emerald-700')}>{bestProjected}</p>
          </div>
        </div>
      </div>

      {/* AI narrator — Explain this forecast */}
      <div className={card} data-testid="forecast-insight">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-500" />
            <h4 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>Explain this forecast</h4>
            {insight && insightCached && <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full', isDark ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-100 text-slate-400')}>cached</span>}
          </div>
          {insight ? (
            <button onClick={() => runInsight({ force: true })} disabled={insightLoading} className={cn('inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border', isDark ? 'border-[#22304d] text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid="forecast-insight-regenerate">
              {insightLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate
            </button>
          ) : (
            <button onClick={() => runInsight()} disabled={insightLoading} className={cn('inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg', isDark ? 'bg-indigo-500/20 text-indigo-200 hover:bg-indigo-500/30' : 'bg-indigo-600 text-white hover:bg-indigo-700')} data-testid="forecast-insight-generate">
              {insightLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Explain in plain English
            </button>
          )}
        </div>
        {insight ? (
          <p className={cn('text-sm leading-relaxed', isDark ? 'text-slate-300' : 'text-slate-600')} data-testid="forecast-insight-text">{insight}</p>
        ) : (
          <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>Get a board-ready read on why <b>{selected.label}</b> is trending this way — and the one move to bend the curve.</p>
        )}
        {insight && (
          <div className="mt-3">
            <button onClick={toggleHistory} className={cn('inline-flex items-center gap-1 text-[11px] font-semibold', isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700')} data-testid="forecast-insight-history-toggle">
              {showHistory ? 'Hide previous reads' : 'Previous reads'}{history.length ? ` (${history.length})` : ''}
            </button>
            {showHistory && (
              <div className="mt-2 space-y-2 border-l-2 pl-3 border-slate-200 dark:border-[#22304d]" data-testid="forecast-insight-history">
                {history.length ? history.map((h, i) => (
                  <div key={i} className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    <span className={cn('block text-[10px] font-semibold uppercase tracking-wide mb-0.5', isDark ? 'text-slate-500' : 'text-slate-400')}>{new Date(h.generated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    {h.insight}
                  </div>
                )) : <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>No earlier reads yet — Regenerate over time to build the story.</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Goal-line overlay */}
      <div className={card} data-testid="forecast-goal">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-4 w-4 text-amber-500" />
          <h4 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>Target LEI goal</h4>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <input
              type="number" min="0" max="100" value={goal}
              onChange={(e) => onGoalChange(e.target.value)}
              placeholder="e.g. 80"
              className={cn('w-24 text-sm px-2.5 py-1.5 rounded-lg border outline-none', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-200 placeholder:text-slate-600' : 'bg-white border-slate-200 text-slate-700 placeholder:text-slate-300')}
              data-testid="forecast-goal-input"
            />
            <span className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>/ 100</span>
          </div>
          {goalStatus ? (
            <div className={cn('flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg', goalStatus.ok ? (isDark ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'bg-rose-500/10 text-rose-300' : 'bg-rose-50 text-rose-700'))} data-testid="forecast-goal-status">
              {goalStatus.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {goalStatus.text}
            </div>
          ) : (
            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>Set a target to overlay it on the chart and see if you're on track.</p>
          )}
          {goalNum != null && goalNum > current && navigate && (
            <button
              onClick={() => modelGoal(goalNum, current)}
              className={cn('inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg', isDark ? 'bg-amber-500/20 text-amber-200 hover:bg-amber-500/30' : 'bg-amber-500 text-white hover:bg-amber-600')}
              data-testid="forecast-model-goal"
            >
              <Target className="h-3.5 w-3.5" /> Model how to reach {goalNum} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* What-if investment simulator */}
      <div className={card} data-testid="forecast-whatif">
        <div className="flex items-center gap-2 mb-2">
          <Sliders className="h-4 w-4 text-cyan-500" />
          <h4 className={cn('text-sm font-bold', isDark ? 'text-slate-100' : 'text-slate-800')}>What-if: extra investment</h4>
        </div>
        {cost ? (
          <>
            <p className={cn('text-xs mb-3', isDark ? 'text-slate-500' : 'text-slate-400')}>Add budget and see the projected LEI lift, based on this {selected.kind}'s efficiency of {money(cost)} per LEI point.</p>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="range" min="0" max={whatIfMax} step="25000" value={whatIf}
                onChange={(e) => setWhatIf(Number(e.target.value))}
                className="flex-1 min-w-[180px] accent-cyan-500"
                data-testid="forecast-whatif-slider"
              />
              <span className={cn('text-sm font-bold tabular-nums w-20 text-right', isDark ? 'text-slate-200' : 'text-slate-700')}>{money(whatIf)}</span>
            </div>
            <div className={cn('mt-3 rounded-xl border p-3 flex items-center justify-between gap-3', isDark ? 'bg-cyan-500/10 border-cyan-500/25' : 'bg-cyan-50 border-cyan-200')} data-testid="forecast-whatif-result">
              <div>
                <p className={cn('text-[11px] font-bold uppercase tracking-wide', isDark ? 'text-cyan-300/80' : 'text-cyan-700/80')}>Projected LEI with this spend</p>
                <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{money(whatIf)} → +{Math.round(leiLift * 10) / 10} pts on top of the {projected} baseline{diminishing ? ' · diminishing returns applied (headroom is limited)' : ''}</p>
              </div>
              <p className="text-2xl font-extrabold tabular-nums text-cyan-500">{adjustedProjected}</p>
            </div>
          </>
        ) : (
          <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')} data-testid="forecast-whatif-nodata">No cost-per-point on record for this {selected.kind}. Set a budget on it (Cohorts / Programs tab) to unlock the simulator.</p>
        )}
      </div>

      {/* Summary */}
      <div className={cn('rounded-2xl border p-4', isDark ? 'bg-purple-500/10 border-purple-500/25 text-purple-200' : 'bg-purple-50 border-purple-200 text-purple-800')} data-testid="forecast-summary">
        <h4 className="text-sm font-bold mb-1">Forecast summary</h4>
        <p className="text-xs"><b>{selected.label}</b>'s LEI is projected to reach <b>{projected}</b> in {horizon} months (a <b>{delta > 0 ? '+' : ''}{delta} point</b> {up ? 'improvement' : 'decline'}), ranging from <b>{worstProjected}</b> (worst case) to <b>{bestProjected}</b> (best case) if the current trajectory holds.</p>
      </div>
    </div>
  );
}
